import { useMemo, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Search } from "lucide-react";
import { Panel } from "@/components/AppShell";
import { api, errorText } from "@/lib/api";

// status -> {label, badge class}
const STATUS = {
  out: { label: "OUT — δεν παίζει", cls: "text-rose-300 bg-rose-500/15 border-rose-500/40" },
  active: { label: "Παίζει σίγουρα", cls: "text-emerald-300 bg-emerald-500/15 border-emerald-500/40" },
  starter: { label: "Βασικός", cls: "text-sky-300 bg-sky-500/15 border-sky-500/40" },
};

function Row({ p, ctx, setCtx }) {
  const [status, setStatus] = useState(p.override || "out");
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/rotation/override", {
        game_id: ctx.game_id, team_id: ctx.team_id, player_id: p.id, status, days: Number(days) || 30,
      });
      setCtx(r.data);
      toast.success(`${p.name}: ${STATUS[status].label} για ${days} μέρες`);
    } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
  };
  const restore = async () => {
    setBusy(true);
    try {
      const r = await api.delete("/rotation/override", { params: { game_id: ctx.game_id, team_id: ctx.team_id, player_id: p.id } });
      setCtx(r.data);
      toast.success(`${p.name}: επαναφορά στο feed`);
    } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
  };

  return (
    <div className="ra-ov-row flex items-center gap-2 px-2 py-1.5 rounded-md border border-slate-800" data-testid={`override-row-${p.id}`}>
      <span className="ra-avatar sm">{p.photo && <img src={p.photo} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />}</span>
      <span className="flex-1 min-w-0">
        <span className="block truncate text-sm font-semibold text-slate-100">{p.jersey && <span className="ra-out-num">#{p.jersey}</span>}{p.name}</span>
        <span className="block truncate text-[11px] text-slate-400">{p.position || "–"} · {p.avg_min} MIN/G</span>
      </span>
      {p.override ? (
        <>
          <span className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 border ${STATUS[p.override].cls}`} data-testid={`override-badge-${p.id}`}>
            {STATUS[p.override].label}{p.override_until ? ` · ως ${p.override_until.slice(5)}` : ""}
          </span>
          <button type="button" onClick={restore} disabled={busy} title="Επαναφορά — ξανά στο feed"
            className="inline-flex items-center gap-1 text-[11px] font-bold rounded px-2 py-1 text-slate-200 border border-slate-600 hover:border-slate-400" data-testid={`override-restore-${p.id}`}>
            <RotateCcw size={12} />Επαναφορά
          </button>
        </>
      ) : (
        <>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`override-status-${p.id}`}
            className="text-[11px] bg-slate-900 border border-slate-700 rounded px-1 py-1 text-slate-200">
            <option value="out">OUT</option>
            <option value="active">Παίζει</option>
            <option value="starter">Βασικός</option>
          </select>
          <input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} data-testid={`override-days-${p.id}`}
            className="w-14 text-[11px] bg-slate-900 border border-slate-700 rounded px-1.5 py-1 text-slate-200" title="Μέρες" />
          <button type="button" onClick={save} disabled={busy}
            className="text-[11px] font-bold rounded px-2 py-1 text-slate-900 bg-slate-200 hover:bg-white disabled:opacity-50" data-testid={`override-save-${p.id}`}>
            {busy ? "…" : "Set"}
          </button>
        </>
      )}
    </div>
  );
}

export function OverridePicker({ ctx, setCtx }) {
  const [q, setQ] = useState("");
  const players = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = term ? ctx.players.filter((p) => p.name.toLowerCase().includes(term)) : ctx.players;
    // Players with an active override first.
    return [...list].sort((a, b) => (b.override ? 1 : 0) - (a.override ? 1 : 0));
  }, [ctx.players, q]);
  const active = ctx.players.filter((p) => p.override).length;

  return (
    <Panel className="p-4" data-testid="rotation-override-picker">
      <h3 className="font-display text-xl font-bold uppercase tracking-wide">Μακροχρόνια διαθεσιμότητα · {ctx.team_code}</h3>
      <p className="text-xs text-slate-400 mb-3">
        Κλίκαρε παίκτη, διάλεξε κατάσταση και αριθμό ημερών. Ισχύει για όλα τα ματς μέχρι να λήξει ή να πατήσεις «Επαναφορά» — υπερισχύει κάθε feed (ESPN/NBA.com/RotoWire).
        {active > 0 && <span className="text-amber-300"> · {active} ενεργά</span>}
      </p>
      <div className="relative mb-2">
        <Search size={14} className="absolute left-2 top-2.5 text-slate-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αναζήτηση παίκτη…" data-testid="override-search"
          className="w-full text-sm bg-slate-900 border border-slate-700 rounded-md pl-7 pr-2 py-1.5 text-slate-100" />
      </div>
      <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
        {players.map((p) => <Row key={p.id} p={p} ctx={ctx} setCtx={setCtx} />)}
      </div>
    </Panel>
  );
}
